// KVKK Deletions API — wired to real backend (S13).
//
// Backend paths:
//   GET    /api/v1/admin/kvkk-deletions                  (?status&userId&page&pageSize)
//   GET    /api/v1/admin/kvkk-deletions/{id}
//   POST   /api/v1/admin/kvkk-deletions/{id}/force-execute  { justification }
//   POST   /api/v1/admin/kvkk-deletions/{id}/cancel         { reason? }   // ISSUE-52
//
// Member self-service:
//   POST   /api/v1/me/kvkk-deletion-request                 { reason }
//   POST   /api/v1/me/kvkk-deletion-request/cancel          { reason }
//
// Notes: there is no admin-side "create deletion request on behalf of user"
// endpoint; `create` still wraps `/me` for the authenticated user. Admin cancel
// now uses the dedicated admin endpoint (ISSUE-52).

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import i18n from '@/i18n';
import { NormalizedApiError } from './errors';
import type {
  CreateKvkkDeletionInput,
  KvkkDeletionListQuery,
  KvkkDeletionListResponse,
  KvkkDeletionRequest,
} from '@features/kvkk/types';

interface KvkkDeletionRequestDto {
  id: string;
  userId: string;
  userEmail: string | null;
  requestedAt: string;
  scheduledDeletionAt: string;
  deletionStatus: string | null;
  processedAt: string | null;
  cancelledAt: string | null;
  cancelledByUserId: string | null;
  cancellationReason: string | null;
  reason: string | null;
  wasForced: boolean;
}

const ADMIN_BASE = '/api/v1/admin/kvkk-deletions';
const ME_BASE = '/api/v1/me/kvkk-deletion-request';

const STATUS_FROM_BE: Record<string, KvkkDeletionRequest['status']> = {
  pending: 'PENDING',
  scheduled: 'PENDING',
  cancelled: 'CANCELLED',
  canceled: 'CANCELLED',
  completed: 'COMPLETED',
  executed: 'COMPLETED',
};

const STATUS_TO_BE: Record<KvkkDeletionRequest['status'], string> = {
  PENDING: 'Pending',
  CANCELLED: 'Cancelled',
  COMPLETED: 'Completed',
};

function mapStatus(s: string | null | undefined): KvkkDeletionRequest['status'] {
  if (!s) return 'PENDING';
  return STATUS_FROM_BE[s.trim().toLowerCase()] ?? 'PENDING';
}

function mapRequest(dto: KvkkDeletionRequestDto): KvkkDeletionRequest {
  const status = mapStatus(dto.deletionStatus);
  return {
    id: dto.id,
    appUserId: dto.userId,
    appUserName:
      dto.userEmail ?? i18n.t('common:fallback.userShort', { id: dto.userId.slice(0, 8) }),
    requestedAt: dto.requestedAt,
    scheduledHardDeleteAt: dto.scheduledDeletionAt,
    cancelable: status === 'PENDING',
    status,
    reason: dto.reason ?? undefined,
    completedAt: dto.processedAt ?? undefined,
  };
}

export const kvkkApi = {
  async list(
    query: KvkkDeletionListQuery = {},
  ): Promise<KvkkDeletionListResponse> {
    const params: Record<string, string | number> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 25,
    };
    if (query.status && query.status !== 'ALL') {
      params.status = STATUS_TO_BE[query.status];
    }
    const res = await http.get<ActionResponse<KvkkDeletionRequestDto>>(
      ADMIN_BASE,
      { params },
    );
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapRequest),
      total: pagination?.totalCount ?? items.length,
      page: pagination?.page ?? (query.page ?? 1),
      pageSize: pagination?.pageSize ?? (query.pageSize ?? 25),
      totalPages: pagination?.totalPages ?? 1,
    };
  },

  async get(id: string): Promise<KvkkDeletionRequest> {
    const dto = unwrap(
      await http.get<ActionResponse<KvkkDeletionRequestDto>>(
        `${ADMIN_BASE}/${id}`,
      ),
    );
    return mapRequest(dto);
  },

  /**
   * Member-facing: request deletion for the currently authenticated user.
   * The admin "create on behalf of" path does not exist in the BE — this is
   * preserved for the member-facing "my deletion request" page.
   * `input.appUserId` is ignored; BE infers the user from the access token.
   */
  async create(input: CreateKvkkDeletionInput): Promise<KvkkDeletionRequest> {
    const newId = unwrap(
      await http.post<ActionResponse<string>>(ME_BASE, {
        reason: input.reason ?? null,
      }),
    );
    return this.get(newId);
  },

  /**
   * Member-facing cancel — used by the "my deletion request" page when the
   * requester themselves is logged in. Hits `/api/v1/me/kvkk-deletion-request/cancel`.
   */
  async cancel(_id: string, reason?: string): Promise<KvkkDeletionRequest> {
    await http.post<ActionResponse<string>>(`${ME_BASE}/cancel`, {
      reason: reason ?? null,
    });
    // BE returns the cancelled request's id — re-fetch via list to find latest.
    const list = await this.list({ page: 1, pageSize: 1, status: 'CANCELLED' });
    return list.items[0] ?? {
      id: _id,
      appUserId: '',
      appUserName: '',
      requestedAt: new Date().toISOString(),
      scheduledHardDeleteAt: new Date().toISOString(),
      cancelable: false,
      status: 'CANCELLED',
    };
  },

  /**
   * ISSUE-52 — Admin-side cancel of a KVKK deletion request on behalf of the
   * user. Requires `kvkk.manage`. Returns the refreshed request DTO.
   */
  async adminCancel(id: string, reason?: string): Promise<KvkkDeletionRequest> {
    await http.post<ActionResponse<string>>(`${ADMIN_BASE}/${id}/cancel`, {
      reason: reason ?? null,
    });
    return this.get(id);
  },

  /**
   * Force-execute (admin-only, danger). The `force` flag is required; the BE
   * has no soft-execute path. When force=false, the FE throws a guardrail
   * error so accidental callers don't bypass the double-confirm UI.
   */
  async execute(
    id: string,
    force = false,
    justification = 'Admin force-execute',
  ): Promise<KvkkDeletionRequest> {
    if (!force) {
      throw new NormalizedApiError({
        status: 400,
        code: 'FORCE_REQUIRED',
        message:
          'KVKK silme yalnızca force-execute ile uygulanabilir. Devam etmek için "Şimdi Sil" butonunu çift onayla.',
      });
    }
    await http.post<ActionResponse<boolean>>(
      `${ADMIN_BASE}/${id}/force-execute`,
      { justification },
    );
    return this.get(id);
  },
};

export type KvkkApi = typeof kvkkApi;
